// Clients — TanStack Query hooks.
// Query keys включают tenantId (см. docs/stack/07-frontend-vite.md §4) + фильтры.
// Keyset-пагинация через useInfiniteQuery (nextCursor).

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, errorText } from '@/lib/api';
import { branchQueryKey, useTenantId } from '@/lib/tenant-context';

import {
  archiveClient,
  clearClientStatus,
  createClient,
  getActiveConsentVersion,
  getClient,
  getFormSettings,
  issueCard,
  listBranches,
  listClientAttendance,
  getClientStats,
  listClients,
  putFormSettings,
  refuseClient,
  revokeCard,
  setClientPrimaryBranch,
  uploadClientPhoto,
  setClientStatus,
  unarchiveClient,
  unrefuseClient,
  releaseFormerPhone,
  updateClient,
  type ActiveConsentVersion,
  type ManualClientStatus,
  type AttendanceDto,
  type BranchListItem,
  type ClientFormSettings,
  type ClientStats,
  type ClientStatus,
  type CreateClientBody,
  type CreateClientResult,
  type GetClientResult,
  type IssueCardBody,
  type ListClientsResult,
  type UpdateClientBody,
} from '../api/clients.api';

const PAGE_SIZE = 25;

export interface ClientsFilters {
  q?: string;
  status?: ClientStatus;
  branchId?: string;
  /** «Без филиала» — страница его передавала, а в типе поля не было. */
  noBranch?: boolean;
  directionId?: string;
  groupId?: string;
  /** План 68 П1: «Вложения» — есть ли файлы во вкладке «Документы». */
  hasDocuments?: boolean;
}

export function clientsKeys(tenantId: string | null) {
  const root = ['clients', tenantId] as const;
  return {
    root,
    list: (filters: ClientsFilters, pageSize: number = PAGE_SIZE) =>
      [...root, 'list', filters, pageSize] as const,
    detail: (id: string) => [...root, 'detail', id] as const,
    attendance: (id: string) => [...root, 'attendance', id] as const,
    branches: [...root, 'branches'] as const,
    consentVersion: [...root, 'consent-version'] as const,
    formSettings: [...root, 'form-settings'] as const,
    stats: (branchId?: string, noBranch?: boolean) =>
      [...root, 'stats', branchId ?? null, noBranch ?? false] as const,
  };
}

export function useClients(
  filters: ClientsFilters = {},
  // Размер страницы. Дефолт 25 — быстрый первый рендер; при активной сортировке
  // страница передаёт 100 (максимум API), чтобы догрузить весь список быстрее.
  pageSize: number = PAGE_SIZE,
): UseInfiniteQueryResult<{ pages: ListClientsResult[]; pageParams: unknown[] }, Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useInfiniteQuery({
    queryKey: keys.list(filters, pageSize),
    queryFn: ({ pageParam }) =>
      listClients({
        ...filters,
        limit: pageSize,
        ...(typeof pageParam === 'string' ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

export function useClient(id: string | null): UseQueryResult<GetClientResult, Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useQuery({
    queryKey: keys.detail(id ?? ''),
    queryFn: () => getClient(id as string),
    enabled: Boolean(id),
  });
}

export function useClientAttendance(id: string | null): UseQueryResult<AttendanceDto[], Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useQuery({
    queryKey: keys.attendance(id ?? ''),
    queryFn: async () => (await listClientAttendance(id as string)).data,
    enabled: Boolean(id),
  });
}

export function useClientStats(
  branchIds?: string[],
  noBranch?: boolean,
): UseQueryResult<ClientStats, Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);
  return useQuery({
    queryKey: keys.stats(branchQueryKey(branchIds ?? []), noBranch),
    queryFn: () => getClientStats(branchIds, noBranch),
    staleTime: 30_000,
  });
}

export function useBranches(): UseQueryResult<BranchListItem[], Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useQuery({
    queryKey: keys.branches,
    queryFn: async () => (await listBranches()).items,
    staleTime: 5 * 60_000, // филиалы меняются редко.
  });
}

export function useActiveConsentVersion(): UseQueryResult<ActiveConsentVersion, Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useQuery({
    queryKey: keys.consentVersion,
    queryFn: () => getActiveConsentVersion(),
    staleTime: 10 * 60_000,
  });
}

export function useFormSettings(): UseQueryResult<ClientFormSettings, Error> {
  const tenantId = useTenantId();
  const keys = clientsKeys(tenantId);

  return useQuery({
    queryKey: keys.formSettings,
    queryFn: () => getFormSettings(),
    staleTime: 5 * 60_000, // настройка меняется редко.
  });
}

export function useUpdateFormSettings(): UseMutationResult<
  ClientFormSettings,
  Error,
  ClientFormSettings
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: (body: ClientFormSettings) => putFormSettings(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.formSettings });
    },
  });
}

export function useCreateClient(): UseMutationResult<CreateClientResult, Error, CreateClientBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateClientBody) => createClient(body),
    onSuccess: () => {
      // Инвалидируем все списки клиентов этого тенанта.
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateClient(): UseMutationResult<
  void,
  Error,
  { id: string; body: UpdateClientBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateClientBody }) => updateClient(id, body),
    onSuccess: (_data, { id }) => {
      // root покрывает все списки; detail — карточка обновлённого клиента.
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

/**
 * Открепить бывший номер от карточки (разбор 17.08).
 *
 * Инвалидируем и списки, и карточку: номер участвует в поиске, а в шапке
 * карточки блок «Бывшие номера» обязан обновиться сразу — иначе кажется, что
 * ничего не произошло.
 */
export function useReleaseFormerPhone(): UseMutationResult<
  { clientId: string; phone: string },
  Error,
  { clientId: string; phone: string }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({ clientId, phone }: { clientId: string; phone: string }) =>
      releaseFormerPhone(clientId, phone),
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(clientId) });
    },
    // Отказ ОБЯЗАН быть видимым (разбор 22.08): защита «номер держит
    // переписка» отвечала 409, а кнопка молчала — оператор четырежды жал
    // «Открепить» и решил, что открепление сломано.
    onError: (e) => {
      if (e instanceof ApiError && e.code === 'client_phone.former_has_conversation') {
        window.alert(
          'Номер держит переписка, привязанная к этой карточке. Сначала перевесьте ' +
            'диалог на нужного человека (Мессенджер → диалог → «Сменить» рядом с ' +
            'клиентом), затем открепите номер.',
        );
        return;
      }
      window.alert(`Открепить не удалось: ${errorText(e)}`);
    },
  });
}

export function useSetClientStatus(): UseMutationResult<
  unknown,
  Error,
  { id: string; status: ManualClientStatus }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: ManualClientStatus }) =>
      setClientStatus(id, status),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useClearClientStatus(): UseMutationResult<unknown, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => clearClientStatus(id),
    onSuccess: (_d, id) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useSetPrimaryBranch(): UseMutationResult<
  unknown,
  Error,
  { id: string; branchId: string }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, branchId }: { id: string; branchId: string }) =>
      setClientPrimaryBranch(id, branchId),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useUploadClientPhoto(): UseMutationResult<
  { photo_url: string },
  Error,
  { id: string; file: File }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadClientPhoto(id, file),
    onSuccess: (_d, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

export function useIssueCard(): UseMutationResult<
  unknown,
  Error,
  { clientId: string; body: IssueCardBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({ clientId, body }: { clientId: string; body: IssueCardBody }) =>
      issueCard(clientId, body),
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.detail(clientId) });
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useRevokeCard(): UseMutationResult<
  unknown,
  Error,
  { clientId: string; cardId: string; reason?: string }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({
      clientId,
      cardId,
      reason,
    }: {
      clientId: string;
      cardId: string;
      reason?: string;
    }) => revokeCard(clientId, cardId, reason !== undefined ? { reason } : {}),
    onSuccess: (_data, { clientId }) => {
      void queryClient.invalidateQueries({ queryKey: keys.detail(clientId) });
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveClient(): UseMutationResult<
  void,
  Error,
  { id: string; archived: boolean }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      archived ? archiveClient(id) : unarchiveClient(id),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}

// «Отказался» / «Вернуть» — ручная пометка статуса. Зеркалит useArchiveClient:
// refused=true → refuse (status='refused' + lock), refused=false → unrefuse (снять lock).
export function useRefuseClient(): UseMutationResult<
  void,
  Error,
  { id: string; refused: boolean }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = clientsKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, refused }: { id: string; refused: boolean }) =>
      refused ? refuseClient(id) : unrefuseClient(id),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: keys.detail(id) });
    },
  });
}
